import React from 'react';
import type { TooltipProps as AntdTooltipProps } from 'antd';
import { Tooltip as AntdTooltip } from 'antd';
import { useTheme } from '@emotion/react';
import Tooltip from 'components/common/tooltip';

/**
 * The migration seam for the raw antd tooltips outside `components/common` (APO-550).
 *
 * Repointing them straight onto the wrapper is not switch-off neutral: it widens the overlay from
 * antd's declared 250px to the wrapper's 320px and wraps string titles in a `pre-wrap` div. So the
 * legacy path stays raw antd with the caller's own props and only the new design gets the wrapper.
 *
 * APO-484 deletes this file at the flip: every site then imports `components/common/tooltip` directly.
 */
const BridgedTooltip: React.FC<AntdTooltipProps> = props => {
  const { isNewDesign } = useTheme();

  if (!isNewDesign) return <AntdTooltip {...props} />;

  const { title, placement, open, onOpenChange, trigger, mouseEnterDelay, children } = props;

  return (
    <Tooltip
      title={title}
      // The wrapper makes placement required where antd defaults it; defaulting here rather than at
      // each call site removes the chance of a missed site silently moving its tooltip.
      placement={placement ?? 'top'}
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      mouseEnterDelay={mouseEnterDelay}>
      {children}
    </Tooltip>
  );
};

export default BridgedTooltip;
